/**
 * Pigment palette — SPEC_ICONOGRAPHY §3.
 *
 * Derived from actual icon pigments rather than from a screen palette. The
 * justification belongs verbatim in the grant application; see
 * ART_DIRECTION_CAST.md.
 */

export const PIGMENT = {
  leadWhite: '#E8E1D4', // ground, inscriptions
  ochre: '#B07A3C',
  cinnabar: '#8C2F27',
  oxblood: '#6B2A32', // the existing WinterMute accent
  malachite: '#3E6B54',
  azurite: '#2F4E6B',
  boneBlack: '#14110F', // ground/background
  gold: '#D4A64B', // RESERVED — see below
} as const;

export type PigmentName = keyof typeof PIGMENT;

/**
 * ⚠️ GOLD IS RESERVED. Do not use it decoratively, and never tint icon points
 * with it.
 *
 * In an icon gold is not a colour, it is uncreated light. It marks exactly one
 * thing, and the authority comes entirely from the scarcity — one gold element
 * among many is decoration, and the signal is gone.
 *
 * **It marks the Watcher's own utterance — spoken, once.** Specifically the
 * Directive (`Directive.tsx`): the line typed in screen space the instant a
 * reply arrives. Nothing else on screen may use it.
 *
 * The Watcher's line used to ALSO get a world-anchored copy, landing on the
 * object it named (`PointText.tsx`) — first gold (sharing this colour, on
 * the theory that one utterance in two places should look like one thing),
 * then ochre (once that theory failed, see below). As of 2026-08-26 that
 * copy is gone entirely: `PointText.tsx` skips `originSession === 'watcher'`
 * fragments outright. Once anchors started committing immediately instead of
 * after a delay (STATUS §17.10), the world copy and the Directive landed
 * within the same few seconds regardless of colour, and just read as the
 * same subtitle shown twice — recolouring it to ochre didn't fix that,
 * removing the second rendering did. See docs/ART_DIRECTION_WATCHER.md.
 *
 * *Previously* this was reserved for high-confidence tracking points — machine
 * confidence rendered as divinity, which was a good idea that was never built
 * (`GOLD_CONFIDENCE` was declared and read by nothing), and the scan overlay
 * has since moved to a deliberately subtler ochre. Reassigned rather than left
 * hypothetical: the Watcher speaking about you is the moment in this piece that
 * actually warrants uncreated light.
 */
export const GOLD_CONFIDENCE = 0.8;

/** Parse '#rrggbb' to 0..1 rgb. */
export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Per-icon primary + shadow pigment. Everything else is derived by alpha. */
export const ICON_PIGMENTS: Record<string, { primary: PigmentName; shadow: PigmentName }> = {
  'she-who-remembers': { primary: 'ochre', shadow: 'oxblood' },
  watchman: { primary: 'cinnabar', shadow: 'boneBlack' },
  'already-decided': { primary: 'azurite', shadow: 'boneBlack' },
  twin: { primary: 'malachite', shadow: 'azurite' },
  forefather: { primary: 'leadWhite', shadow: 'azurite' },
};
